import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'
import type { LotteryRound } from '../services/lotteryService'
import { lotteryService } from '../services/lotteryService'
import { useWallet } from '../contexts/WalletContext'
import { getTenantIdFromConfig } from '../utils/tenantUtils'
import { useTenantThemeConfig } from '../hooks/useTenantThemeConfig'
import { withApiBaseUrl } from '../lib/apiBaseUrl'
import { resolvePrizeTypeLabel } from '../utils/prizeTypeLabels'
import { buildPayoutRatesTableData } from '../utils/payoutRatesTable'

interface BettingPackage {
  code: string
  name: string
  packageType: 'RATE_BASED' | 'SET_BASED'
  prizeConfigs?: Array<{
    prizeTypeCode: string
    prizeTypeName: string
    payoutRate: number
    discount: number
    minBet?: number
    maxBet?: number
    displayOrder?: number
    isActive?: boolean
  }>
  setConfig?: {
    fixedPrice: number
    fixedPayout: number
    discount: number
  }
  [key: string]: any
}

interface BetItem {
  id: string
  number: string
  betType: string
  betTypeLabel: string
  amount: number
  multiplier: number
  discount: number
  payout: number
}

interface BetLimitInfo {
  limitType: string
  cap: number
  currentTotal: number
  requestedAmount: number
  nextTotal: number
  remainingAllowed: number
  remainingAfterItem: number
  exceededBy: number
  suggestedAmount: number
  minimumBet?: number
}

interface ValidatedBetItemState {
  isValid: boolean
  isBlocked?: boolean
  reason?: string
  payoutRate?: number
  discount?: number
  potentialPayout?: number
  amount?: number
  limitInfo?: BetLimitInfo | null
  betLimits?: {
    minBet: number
    maxBet: number
  } | null
}

interface LotteryBetSideRightProps {
  amount?: string
  setAmount?: (amount: string) => void
  selectedPackage?: BettingPackage
  round?: LotteryRound
  betItems?: BetItem[]
  removeBetItem?: (id: string) => void
  updateBetAmount?: (id: string, amount: number) => void
  clearAllBetItems?: () => void
  selectedPrizeCodes?: string[]
  submitBetItems?: () => void | Promise<void>
  isSubmittingBet?: boolean
  /** จำนวนแพ็กเกจที่มีให้เลือก (ถ้ามีแค่ 1 จะ disable ปุ่มเปลี่ยนแปลงราคาแทง) */
  packageCount?: number
}

const LotteryBetSideRight: React.FC<LotteryBetSideRightProps> = ({
  selectedPackage,
  round,
  betItems = [],
  removeBetItem,
  updateBetAmount,
  clearAllBetItems,
  submitBetItems,
  isSubmittingBet = false,
  packageCount
}) => {
  const navigate = useNavigate()
  const { walletData, isLoadingWallet, formatCurrency } = useWallet()
  const { themeConfig } = useTenantThemeConfig()
  const tenantId = getTenantIdFromConfig(themeConfig)
  const walletBalance = (walletData?.balance?.main ?? 0) + (walletData?.balance?.bonus ?? 0)
  // useRef สำหรับ focus ไปที่ input แรก
  const firstInputRef = useRef<HTMLInputElement>(null)

  // Check if this is a Yeekee lottery
  const isYeekee = useMemo(() => {
    if (!round) return false
    const huayTypeName = round.huayTypeName || ''
    const huayName = round.huayName || ''
    return huayTypeName.includes('ยี่กี') || huayTypeName.includes('ยี') || 
           huayName.includes('ยี่กี') || huayName.includes('ยี')
  }, [round])

  const yeekeeNumberDigits = useMemo(() => {
    const configured = Number(round?.yeekeeConfig?.numberDigits)
    return Number.isInteger(configured) && configured >= 5 && configured <= 8 ? configured : 5
  }, [round?.yeekeeConfig?.numberDigits])

  // State for Yeekee predictions and participants
  const [yeekeeShots, setYeekeeShots] = useState<Array<{
    _id?: string
    number: string
    displayPhoneNumber?: string
    username?: string
    shotAt: string
    shooterType?: string
  }>>([])
  const [loadingShots, setLoadingShots] = useState(false)
  const hasLoadedShotsOnce = useRef(false)
  const [countdown, setCountdown] = useState<string>('00:00:00')
  const [predictionNumber, setPredictionNumber] = useState<string>('')

  useEffect(() => {
    if (!isYeekee) return
    setPredictionNumber((prev) => prev.slice(0, yeekeeNumberDigits))
  }, [isYeekee, yeekeeNumberDigits])

  const handleRandomPrediction = useCallback(() => {
    setPredictionNumber(
      Array.from({ length: yeekeeNumberDigits }, () => String(Math.floor(Math.random() * 10))).join('')
    )
  }, [yeekeeNumberDigits])

  // State สำหรับจำนวนเงินที่จะตั้งเท่ากันหมด
  const [bulkAmount, setBulkAmount] = useState<string>('')

  // State สำหรับการไฮไลท์รายการซ้ำ
  const [duplicateHighlight, setDuplicateHighlight] = useState<{
    enabled: boolean
    sameAmountItems: string[]
    differentAmountItems: string[]
  }>({
    enabled: false,
    sameAmountItems: [],
    differentAmountItems: []
  })
  const [inlineValidationByItemId, setInlineValidationByItemId] = useState<Record<string, ValidatedBetItemState>>({})
  const [isInlineValidating, setIsInlineValidating] = useState(false)
  const inlineValidationRequestRef = useRef(0)

  // จัดกลุ่มรายการเดิมพันตามประเภท
  const groupedBetItems = useMemo(() => {
    const groups: { [key: string]: BetItem[] } = {}
    betItems.forEach(item => {
      if (!groups[item.betType]) {
        groups[item.betType] = []
      }
      groups[item.betType].push(item)
    })
    return groups
  }, [betItems])


  // เพิ่มลำดับการแสดงผลตามที่ผู้ใช้ระบุ (ตรงกับ prizeTypeCode / betType จากเลขชุดและฝั่งซ้าย)
  const betTypeDisplayOrder: { [key: string]: number } = {
    top2: 1, // 2 ตัวบน
    down2: 2, // 2 ตัวล่าง
    tod2: 3, // 2 ตัวโต๊ด
    tod2reverse: 4, // 2 ตัวกลับ
    top3: 5, // 3 ตัวบน
    down3: 6, // 3 ตัวล่าง
    tod3: 7, // 3 ตัวโต๊ด
    tod3reverse: 8, // 3 ตัวกลับ
    top1: 9, // วิ่งบน
    down1: 10, // วิ่งล่าง
    top4: 11, // 4 ตัวบน
    tod4: 12, // 4 ตัวโต๊ด
    tod5: 13, // 5 ตัวโต๊ด
    puk1: 14, // ปักหลักหน่วย
    puk10: 15, // ปักหลักสิบ
    puk100: 16, // ปักหลักร้อย
    '4 ตัวบน': 11,
    '4 ตัวโต๊ด': 12,
    '5 ตัวโต๊ด': 13,
    'ปักหลักหน่วย': 14,
    'ปักหลักสิบ': 15,
    'ปักหลักร้อย': 16,
    top3pick1group: 17, // เลขปัก
    top5fourgroup: 18 // 4-5 ตัว
  }

  // ฟังก์ชันเรียงลำดับ betType ตามที่กำหนด
  const getSortedBetTypes = (grouped: { [key: string]: BetItem[] }) => {
    return Object.entries(grouped).sort(([betTypeA], [betTypeB]) => {
      const orderA = betTypeDisplayOrder[betTypeA] || 999
      const orderB = betTypeDisplayOrder[betTypeB] || 999
      return orderA - orderB
    })
  }

  const handleAmountChange = (itemId: string, newAmount: string) => {
    if (!updateBetAmount) return
    const amountValue = parseInt(newAmount) || 0
    updateBetAmount(itemId, amountValue)
  }

  // ฟังก์ชันตรวจสอบรายการซ้ำ (เฉพาะในกลุ่มเดียวกัน)
  const checkDuplicateItems = useCallback(() => {
    if (betItems.length === 0) {
      toast('ไม่มีรายการเดิมพันให้ตรวจสอบ', {
        position: 'top-center',
        icon: 'ℹ️'
      })
      return
    }

    const groupedItems: { [key: string]: { [key: string]: BetItem[] } } = {}
    const sameAmountItems: string[] = []
    const differentAmountItems: string[] = []
    const duplicateGroupInfo: { [key: string]: any } = {}

    // จัดกลุ่มรายการตาม betType ก่อน
    betItems.forEach(item => {
      if (!groupedItems[item.betType]) {
        groupedItems[item.betType] = {}
      }
      // จัดกลุ่มย่อยตาม number ในแต่ละ betType
      if (!groupedItems[item.betType][item.number]) {
        groupedItems[item.betType][item.number] = []
      }
      groupedItems[item.betType][item.number].push(item)
    })

    // ตรวจสอบการซ้ำในแต่ละกลุ่ม betType
    Object.entries(groupedItems).forEach(([betType, numberGroups]) => {
      const betTypeLabel = betItems.find(item => item.betType === betType)?.betTypeLabel || betType

      Object.entries(numberGroups).forEach(([number, items]) => {
        if (items.length > 1) {
          // มีรายการซ้ำในกลุ่มนี้
          if (!duplicateGroupInfo[betType]) {
            duplicateGroupInfo[betType] = {
              label: betTypeLabel,
              duplicateNumbers: [],
              totalItems: 0
            }
          }

          duplicateGroupInfo[betType].duplicateNumbers.push({
            number: number,
            count: items.length,
            amounts: items.map(item => item.amount)
          })
          duplicateGroupInfo[betType].totalItems += items.length

          // ตรวจสอบว่ายอดเงินเท่ากันหรือไม่
          const amounts = [...new Set(items.map(item => item.amount))]

          if (amounts.length === 1) {
            // ยอดเงินเท่ากันหมด
            items.forEach(item => sameAmountItems.push(item.id))
          } else {
            // ยอดเงินไม่เท่ากัน
            items.forEach(item => differentAmountItems.push(item.id))
          }
        }
      })
    })

    // อัพเดท state
    setDuplicateHighlight({
      enabled: true,
      sameAmountItems,
      differentAmountItems
    })

    // แสดงผลลัพธ์
    const totalDuplicates = sameAmountItems.length + differentAmountItems.length
    if (totalDuplicates === 0) {
      toast.success('✅ ไม่พบเลขซ้ำในทุกกลุ่ม', {
        position: 'top-center'
      })
      setDuplicateHighlight({
        enabled: false,
        sameAmountItems: [],
        differentAmountItems: []
      })
    } else {
      // สร้างข้อความแสดงรายละเอียดกลุ่มที่มีเลขซ้ำ
      const groupDetails = Object.entries(duplicateGroupInfo)
        .map(([, info]: [string, any]) => `${info.label}: ${info.duplicateNumbers.length} เลข`)
        .join(', ')

      const sameAmountCount = sameAmountItems.length
      const diffAmountCount = differentAmountItems.length

      toast(
        `🔍 พบเลขซ้ำ ${totalDuplicates} รายการ\n📊 กลุ่มที่มีซ้ำ: ${groupDetails}\n💰 เงินเท่ากัน: ${sameAmountCount} | เงินไม่เท่ากัน: ${diffAmountCount}`,
        {
          position: 'top-center',
          duration: 5000,
          icon: '⚠️'
        }
      )
    }
  }, [betItems])

  // ฟังก์ชันตัดเลขซ้ำในแต่ละกลุ่ม
  const removeDuplicateItems = useCallback(() => {
    if (!removeBetItem) return

    if (betItems.length === 0) {
      toast('ไม่มีรายการเดิมพันให้ตัด', {
        position: 'top-center',
        icon: 'ℹ️'
      })
      return
    }

    const groupedItems: { [key: string]: { [key: string]: BetItem[] } } = {}
    let itemsToRemove: BetItem[] = []
    const duplicateGroupInfo: { [key: string]: any } = {}

    // จัดกลุ่มรายการตาม betType ก่อน
    betItems.forEach(item => {
      if (!groupedItems[item.betType]) {
        groupedItems[item.betType] = {}
      }
      // จัดกลุ่มย่อยตาม number ในแต่ละ betType
      if (!groupedItems[item.betType][item.number]) {
        groupedItems[item.betType][item.number] = []
      }
      groupedItems[item.betType][item.number].push(item)
    })

    // ตรวจสอบและหารายการที่ต้องลบในแต่ละกลุ่ม
    Object.entries(groupedItems).forEach(([betType, numberGroups]) => {
      const betTypeLabel = betItems.find(item => item.betType === betType)?.betTypeLabel || betType

      Object.entries(numberGroups).forEach(([, items]) => {
        if (items.length > 1) {
          // มีรายการซ้ำในกลุ่มนี้ - เก็บไว้แค่รายการแรก ตัดที่เหลือ
          const itemsToDelete = items.slice(1)
          itemsToRemove.push(...itemsToDelete)

          // เก็บข้อมูลสำหรับแสดงผล
          if (!duplicateGroupInfo[betType]) {
            duplicateGroupInfo[betType] = {
              label: betTypeLabel,
              duplicateNumbers: [],
              removedCount: 0
            }
          }

          duplicateGroupInfo[betType].duplicateNumbers.push({
            number: items[0].number,
            total: items.length,
            toRemove: itemsToDelete.length
          })
          duplicateGroupInfo[betType].removedCount += itemsToDelete.length
        }
      })
    })

    if (itemsToRemove.length === 0) {
      toast('✅ ไม่พบเลขซ้ำในทุกกลุ่มให้ตัด', {
        position: 'top-center',
        icon: 'ℹ️'
      })
      return
    }

    // สร้างรายละเอียดสำหรับ confirmation dialog
    const groupSummary = Object.entries(duplicateGroupInfo)
      .map(([, info]: [string, any]) => `<li><strong>${info.label}:</strong> ตัด ${info.removedCount} รายการ จาก ${info.duplicateNumbers.length} เลข</li>`)
      .join('')

    const duplicateCount = itemsToRemove.length
    const affectedGroups = Object.keys(duplicateGroupInfo).length

    Swal.fire({
      title: 'ยืนยันการตัดเลขซ้ำในกลุ่ม',
      html: `
        <div style="text-align: left;">
          <p><strong>📊 สรุปการตัดเลขซ้ำ:</strong></p>
          <ul style="margin: 10px 0; padding-left: 20px;">
            ${groupSummary}
          </ul>
          <hr>
          <p><strong>📋 รวม:</strong></p>
          <p>• กลุ่มที่มีเลขซ้ำ: ${affectedGroups} กลุ่ม</p>
          <p>• รายการที่จะตัด: ${duplicateCount} รายการ</p>
          <p style="color: #666; font-size: 14px; margin-top: 15px;">
            <i class="fas fa-info-circle"></i> หมายเหตุ: จะเก็บไว้แค่ 1 รายการต่อเลขในแต่ละกลุ่ม
          </p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'ตัดเลขซ้ำ',
      cancelButtonText: 'ยกเลิก',
      width: '500px'
    }).then(result => {
      if (result.isConfirmed) {
        // ลบรายการที่ซ้ำ
        itemsToRemove.forEach(item => {
          removeBetItem(item.id)
        })

        // ปิดการไฮไลท์ถ้าเปิดอยู่
        setDuplicateHighlight({
          enabled: false,
          sameAmountItems: [],
          differentAmountItems: []
        })

        // สร้างข้อความสรุปผล
        const resultSummary = Object.entries(duplicateGroupInfo)
          .map(([, info]: [string, any]) => `${info.label}: ${info.removedCount} รายการ`)
          .join(', ')

        toast.success(
          `✅ ตัดเลขซ้ำเสร็จสิ้น\n📊 ${resultSummary}\n🗑️ รวมลบ ${duplicateCount} รายการจาก ${affectedGroups} กลุ่ม`,
          {
            position: 'top-center',
            duration: 4000
          }
        )
      }
    })
  }, [betItems, removeBetItem])

  // ฟังก์ชันสำหรับใส่ราคาเท่ากันหมด
  const handleBulkAmountUpdate = (amountValue: string | number) => {
    if (!updateBetAmount) return
    const amountNum = typeof amountValue === 'string' ? parseInt(amountValue) : amountValue
    if (amountNum > 0 && betItems.length > 0) {
      betItems.forEach(item => {
        updateBetAmount(item.id, amountNum)
      })
      setBulkAmount('')
    }
  }

  // ฟังก์ชันปิดการไฮไลท์
  const clearDuplicateHighlight = useCallback(() => {
    setDuplicateHighlight({
      enabled: false,
      sameAmountItems: [],
      differentAmountItems: []
    })
    toast('ปิดการแสดงรายการซ้ำแล้ว', {
      position: 'top-center',
      icon: 'ℹ️'
    })
  }, [])

  // ฟังก์ชันสำหรับจัดรูปแบบข้อมูลรอบให้อ่านง่าย
  const formatRoundDisplay = (round: LotteryRound | undefined): string => {
    if (!round) return 'ไม่ระบุ'
    
    // ลำดับความสำคัญ: roundNumber > drawDate + drawTime > _id (fallback)
    if (round.roundNumber) {
      // ถ้ามี drawDate ให้แสดงด้วย
      if (round.drawDate) {
        try {
          const drawDate = new Date(round.drawDate)
          const formattedDate = drawDate.toLocaleDateString('th-TH', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          })
          return `รอบที่ ${round.roundNumber} (${formattedDate})`
        } catch (e) {
          return `รอบที่ ${round.roundNumber}`
        }
      }
      return `รอบที่ ${round.roundNumber}`
    }
    
    // ถ้าไม่มี roundNumber แต่มี drawDate
    if (round.drawDate) {
      try {
        const drawDate = new Date(round.drawDate)
        const formattedDate = drawDate.toLocaleDateString('th-TH', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        const drawTime = round.drawTime ? ` ${round.drawTime}` : ''
        return `รอบ ${formattedDate}${drawTime}`
      } catch (e) {
        // Fallback to _id if date parsing fails
        return `รอบ ${round._id.substring(0, 8)}...`
      }
    }
    
    // Fallback: ใช้ _id แต่แสดงแค่ส่วนแรกเพื่อให้สั้นลง
    return `รอบ ${round._id.substring(0, 8)}...`
  }

  // แปลง reason ของเลขอั้นจ่ายจากภาษาอังกฤษเป็นภาษาไทย (สำหรับ modal)
  const formatBlockedReason = (reason: string | undefined): string => {
    if (!reason) return 'อั้นจ่าย'
    const r = reason.trim()
    const lower = r.toLowerCase()
    const formatPrizeTypeLabel = (code: string | undefined): string => {
      const key = String(code || '').trim().toUpperCase()
      const labels: Record<string, string> = {
        '3_DIGIT_TOP': '3 ตัวบน',
        '3_DIGIT_BOTTOM': '3 ตัวล่าง',
        '3_DIGIT_TOD': '3 ตัวโต๊ด',
        '2_DIGIT_TOP': '2 ตัวบน',
        '2_DIGIT_BOTTOM': '2 ตัวล่าง',
        '2_DIGIT_TOD': '2 ตัวโต๊ด',
        'RUNNING_TOP': 'วิ่งบน',
        'RUNNING_BOTTOM': 'วิ่งล่าง',
        '4_DIGIT_TOP': '4 ตัวบน',
        '4_DIGIT_TOD': '4 ตัวโต๊ด',
        '5_DIGIT_TOD': '5 ตัวโต๊ด',
        'FIXED_POSITION_HUNDREDS': 'ปักหลักร้อย',
        'FIXED_POSITION_TENS': 'ปักหลักสิบ',
        'FIXED_POSITION_ONES': 'ปักหลักหน่วย'
      }
      return labels[key] || code || '-'
    }
    if (lower.includes('some bet items are no longer valid')) {
      return 'มีบางรายการไม่สามารถซื้อได้แล้ว กรุณาลบรายการที่มีปัญหาออกแล้วลองใหม่'
    }
    if (
      lower.includes('account_betting_blocked') ||
      lower.includes('blocked from betting') ||
      lower.includes('กลุ่มจำกัดการแทง') ||
      lower.includes('บัญชีนี้ถูกบล็อกการแทง') ||
      lower.includes('บัญชีนี้ยังไม่สามารถทำรายการแทงได้')
    ) {
      return 'บัญชีนี้ยังไม่สามารถทำรายการแทงได้ กรุณาติดต่อเจ้าหน้าที่'
    }
    if (r === 'Number has reached its betting limit') return 'เลขถึงวงเงินแทงแล้ว (ไม่รับแทง)'
    if (lower.includes('would exceed total betting limit')) return 'เกินวงเงินแทงรวมของประเภทรางวัลนี้'
    if (lower.includes('prize type is temporarily paused')) return 'ประเภทรางวัลนี้หยุดรับชั่วคราว'
    if (lower.includes('prize type not found')) return 'ไม่พบประเภทรางวัลในรอบนี้'
    if (lower.includes('not active')) return 'ประเภทรางวัลนี้ไม่เปิดรับ'
    if (lower.includes('betting package not found') || lower.includes('package not found')) return 'ไม่พบแพ็กเกจการเดิมพัน กรุณาเลือกแพ็กเกจใหม่'
    if (lower.includes('bet amount must be between') || lower.includes('จำนวนเงินเดิมพันสำหรับ')) {
      return 'ยอดเดิมพันของรายการนี้เกินเงื่อนไขที่กำหนด กรุณาปรับยอดแล้วลองอีกครั้ง'
    }
    if (lower.includes('prize type') && lower.includes('not available')) {
      const match = r.match(/Prize type\s+([A-Z0-9_]+)\s+not available/i)
      if (match) return `ไม่พบประเภทรางวัล ${formatPrizeTypeLabel(match[1])}`
      return 'ไม่พบประเภทรางวัลในรอบนี้'
    }
    if (lower.includes('not available in package')) {
      const match = r.match(/Prize type\s+([A-Z0-9_]+)\s+not available in package\s+([A-Z0-9_]+)/i)
      if (match) return `ประเภทรางวัล ${formatPrizeTypeLabel(match[1])} ไม่รองรับในแพ็กเกจ ${match[2]}`
      return 'ประเภทรางวัลนี้ไม่รองรับในแพ็กเกจที่เลือก'
    }
    if (lower.includes('is currently disabled')) {
      const match = r.match(/Prize type\s+([A-Z0-9_]+)\s+is currently disabled/i)
      if (match) return `ประเภทรางวัล ${formatPrizeTypeLabel(match[1])} ปิดรับแทงอยู่ในขณะนี้`
      return 'ประเภทรางวัลนี้ปิดรับแทงอยู่ในขณะนี้'
    }
    if (lower.includes('invalid number length')) {
      const match = r.match(/Invalid number length for\s+([A-Z0-9_]+)\. Expected\s+(\d+)\s+digits/i)
      if (match) return `${formatPrizeTypeLabel(match[1])} ต้องมี ${match[2]} หลัก`
      return 'จำนวนหลักของเลขที่กรอกไม่ถูกต้อง'
    }
    if (lower.includes('number is blocked or full')) return 'เลขนี้ถูกปิดรับหรือเต็มแล้ว'
    if (lower.includes('missing prizetypecode')) return 'ไม่พบประเภทรางวัลของรายการแทง'
    return r
  }

  const formatAmountDisplay = (value: number | undefined, forceDecimals = false): string => {
    const numericValue = Number(value || 0)
    return numericValue.toLocaleString('th-TH', {
      minimumFractionDigits: forceDecimals ? 2 : 0,
      maximumFractionDigits: 2
    })
  }

  const getInlineValidationNotice = (validatedItem?: ValidatedBetItemState | null) => {
    if (!validatedItem || validatedItem.isValid) {
      return null
    }

    const translatedReason = formatBlockedReason(validatedItem.reason)
    const limitInfo = validatedItem.limitInfo || null

    if (
      String(validatedItem.reason || '').toLowerCase().includes('bet amount must be between')
      || String(validatedItem.reason || '').includes('จำนวนเงินเดิมพันสำหรับ')
    ) {
      const maxBet = Number(validatedItem.betLimits?.maxBet || 0)
      const minBet = Number(validatedItem.betLimits?.minBet || 0)
      const currentAmount = Number(validatedItem.amount || 0)
      if (maxBet > 0 && currentAmount > maxBet) {
        return {
          title: translatedReason,
          detail: `ยอดสูงสุดที่ซื้อได้สำหรับรายการนี้คือ ${formatAmountDisplay(maxBet)} บาท`,
          suggestedAmount: maxBet
        }
      }
      if (minBet > 0 && currentAmount > 0 && currentAmount < minBet) {
        return {
          title: translatedReason,
          detail: `ยอดขั้นต่ำที่ซื้อได้สำหรับรายการนี้คือ ${formatAmountDisplay(minBet)} บาท`,
          suggestedAmount: minBet
        }
      }
      return {
        title: translatedReason,
        detail: 'รายการนี้ยังไม่ผ่านเงื่อนไขของระบบ กรุณาปรับยอดก่อนส่งโพย',
        suggestedAmount: 0
      }
    }

    if (!limitInfo) {
      return {
        title: translatedReason,
        detail: '',
        suggestedAmount: 0
      }
    }

    if (limitInfo.remainingAllowed > 0) {
      const canAutoAdjust = Number(limitInfo.suggestedAmount || 0) >= Number(limitInfo.minimumBet || 1)
      return {
        title: translatedReason,
        detail: canAutoAdjust
          ? `รายการนี้ซื้อได้สูงสุดอีก ${formatAmountDisplay(limitInfo.remainingAllowed)} บาท`
          : 'รายการนี้ยังไม่ผ่านเงื่อนไขของระบบ กรุณาปรับยอดก่อนส่งโพย',
        suggestedAmount: canAutoAdjust ? Number(limitInfo.suggestedAmount || 0) : 0
      }
    }

    return {
      title: translatedReason,
      detail: 'รายการนี้ไม่สามารถซื้อเพิ่มได้ในขณะนี้',
      suggestedAmount: 0
    }
  }

  const hasFreshInlineValidation = (item: BetItem, validatedItem?: ValidatedBetItemState | null) => {
    if (!validatedItem) return false
    const validatedAmount = Number(validatedItem.amount)
    return Number.isFinite(validatedAmount) && validatedAmount === Number(item.amount)
  }

  const isHardBlockedValidation = (validatedItem?: ValidatedBetItemState | null) => {
    if (!validatedItem?.isBlocked) return false
    const rawReason = String(validatedItem.reason || '').toLowerCase()
    return rawReason.includes('number is blocked or full')
      || rawReason.includes('number has reached its betting limit')
      || rawReason.includes('เลขนี้ถูกปิดรับหรือเต็มแล้ว')
      || rawReason.includes('เลขถึงวงเงินแทงแล้ว')
  }

  const totalBetAmount = useMemo(() => {
    return betItems.reduce((total, item) => {
      const validatedItem = inlineValidationByItemId[item.id]
      const hasFreshValidation = hasFreshInlineValidation(item, validatedItem)
      if (!hasFreshValidation) return total + Number(item.amount ?? 0)
      if (!validatedItem?.isValid) return total
      return total + Number(validatedItem.amount ?? item.amount ?? 0)
    }, 0)
  }, [betItems, inlineValidationByItemId])

  /** ส่ง API ใช้ 3_DIGIT_TOP / 2_DIGIT_TOP แทน REVERSE (เลขถูกขยายเป็น 6/2 ชุดแล้ว) */
  const prizeTypeCodeForApi = (prizeTypeCode: string): string => {
    if (prizeTypeCode === '3_DIGIT_REVERSE') return '3_DIGIT_TOP'
    if (prizeTypeCode === '2_DIGIT_REVERSE') return '2_DIGIT_TOP'
    return prizeTypeCode
  }

  // Mapping function: betType (frontend) -> prizeTypeCode (backend)
  const betTypeToPrizeTypeCode = (betType: string, betTypeLabel?: string): string => {
    // Handle fixed position digits
    if (betTypeLabel === 'ปักหลักร้อย') return 'FIXED_POSITION_HUNDREDS'
    if (betTypeLabel === 'ปักหลักสิบ') return 'FIXED_POSITION_TENS'
    if (betTypeLabel === 'ปักหลักหน่วย') return 'FIXED_POSITION_ONES'
    
    // Handle other bet types
    const mapping: { [key: string]: string } = {
      'top3': '3_DIGIT_TOP',
      'down3': '3_DIGIT_BOTTOM',
      'tod3': '3_DIGIT_TOD',
      'tod3reverse': '3_DIGIT_REVERSE',
      'top2': '2_DIGIT_TOP',
      'down2': '2_DIGIT_BOTTOM',
      'tod2': '2_DIGIT_TOD',
      'tod2reverse': '2_DIGIT_REVERSE',
      'top1': 'RUNNING_TOP',
      'down1': 'RUNNING_BOTTOM',
      'top4': '4_DIGIT_TOP',
      'tod4': '4_DIGIT_TOD',
      'tod5': '5_DIGIT_TOD',
      'puk1': 'FIXED_POSITION_ONES',
      'puk10': 'FIXED_POSITION_TENS',
      'puk100': 'FIXED_POSITION_HUNDREDS'
    }
    
    return mapping[betType] || betType.toUpperCase()
  }

  const buildOptimisticValidationState = useCallback(() => {
    return betItems.reduce<Record<string, ValidatedBetItemState>>((acc, item) => {
      const prizeTypeCode = prizeTypeCodeForApi(betTypeToPrizeTypeCode(item.betType, item.betTypeLabel))
      const prizeConfig = selectedPackage?.prizeConfigs?.find(
        (config) => String(config.prizeTypeCode || '').toUpperCase() === prizeTypeCode
      )
      const payoutRate = Number(prizeConfig?.payoutRate ?? item.multiplier ?? 0)
      const discount = Number(prizeConfig?.discount ?? item.discount ?? 0)
      const minBet = Number(prizeConfig?.minBet ?? 1)
      const maxBet = Number(prizeConfig?.maxBet ?? 100000)
      const amount = Number(item.amount || 0)
      const amountOutOfRange = amount > 0 && (amount < minBet || amount > maxBet)

      acc[item.id] = {
        isValid: !amountOutOfRange,
        reason: amountOutOfRange
          ? `จำนวนเงินเดิมพันสำหรับ ${resolvePrizeTypeLabel(prizeTypeCode)} ต้องอยู่ระหว่าง ${minBet} ถึง ${maxBet} บาท`
          : undefined,
        payoutRate,
        discount,
        potentialPayout: amount * payoutRate,
        amount,
        betLimits: { minBet, maxBet }
      }
      return acc
    }, {})
  }, [betItems, selectedPackage?.prizeConfigs])

  useEffect(() => {
    if (!round?._id || betItems.length === 0) {
      inlineValidationRequestRef.current += 1
      setInlineValidationByItemId({})
      setIsInlineValidating(false)
      return
    }

    const requestId = inlineValidationRequestRef.current + 1
    inlineValidationRequestRef.current = requestId
    setInlineValidationByItemId(buildOptimisticValidationState())
    setIsInlineValidating(true)

    const abortController = new AbortController()

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await lotteryService.validateBetItems(
          round._id,
          selectedPackage?.code || null,
          betItems.map((item) => {
            const code = (() => {
              if (item.betTypeLabel === 'ปักหลักร้อย') return 'FIXED_POSITION_HUNDREDS'
              if (item.betTypeLabel === 'ปักหลักสิบ') return 'FIXED_POSITION_TENS'
              if (item.betTypeLabel === 'ปักหลักหน่วย') return 'FIXED_POSITION_ONES'

              const mapping: { [key: string]: string } = {
                top3: '3_DIGIT_TOP',
                down3: '3_DIGIT_BOTTOM',
                tod3: '3_DIGIT_TOD',
                tod3reverse: '3_DIGIT_REVERSE',
                top2: '2_DIGIT_TOP',
                down2: '2_DIGIT_BOTTOM',
                tod2: '2_DIGIT_TOD',
                tod2reverse: '2_DIGIT_REVERSE',
                top1: 'RUNNING_TOP',
                down1: 'RUNNING_BOTTOM',
                top4: '4_DIGIT_TOP',
                tod4: '4_DIGIT_TOD',
                tod5: '5_DIGIT_TOD',
                puk1: 'FIXED_POSITION_ONES',
                puk10: 'FIXED_POSITION_TENS',
                puk100: 'FIXED_POSITION_HUNDREDS'
              }

              return mapping[item.betType] || item.betType.toUpperCase()
            })()
            return {
              prizeTypeCode: code === '3_DIGIT_REVERSE' ? '3_DIGIT_TOP' : code === '2_DIGIT_REVERSE' ? '2_DIGIT_TOP' : code,
              number: item.number,
              amount: item.amount
            }
          }),
          { signal: abortController.signal }
        )

        if (inlineValidationRequestRef.current !== requestId) {
          return
        }

        const validatedItems = Array.isArray(result?.data?.validatedItems) ? result.data.validatedItems : []
        const nextValidationState = betItems.reduce<Record<string, ValidatedBetItemState>>((acc, item, index) => {
          acc[item.id] = validatedItems[index] || { isValid: true }
          return acc
        }, {})

        setInlineValidationByItemId(nextValidationState)
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          return
        }
        if (inlineValidationRequestRef.current === requestId) {
          console.error('Inline validate-bet failed:', error)
          setInlineValidationByItemId({})
        }
      } finally {
        if (inlineValidationRequestRef.current === requestId) {
          setIsInlineValidating(false)
        }
      }
    }, 450)

    return () => {
      window.clearTimeout(timeoutId)
      abortController.abort()
    }
  }, [betItems, buildOptimisticValidationState, round?._id, selectedPackage?.code])

  // ฟังก์ชันสำหรับยืนยันการส่งโพย
  const handleSubmitBetConfirmation = useCallback(async () => {
    if (isSubmittingBet) {
      return
    }
    if (!submitBetItems || betItems.length === 0) {
      toast('ไม่มีรายการเดิมพันให้ส่ง', {
        position: 'top-center',
        duration: 3000,
        icon: '⚠️'
      })
      return
    }

    if (!round?._id) {
      toast('ไม่พบข้อมูลรอบหวย', {
        position: 'top-center',
        duration: 3000,
        icon: '⚠️'
      })
      return
    }

    if (totalBetAmount > walletBalance) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยอดเงินไม่เพียงพอ',
        html: `ยอดเครดิตคงเหลือ <strong>${formatCurrency(walletBalance)}</strong> บาท น้อยกว่ายอดที่ต้องการแทง <strong>${totalBetAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong> บาท<br><br>กรุณาเติมเงินหรือลดจำนวนเดิมพัน`,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#fd7e14'
      })
      return
    }

    // แสดง loading
    Swal.fire({
      title: 'กำลังตรวจสอบ...',
      text: 'กรุณารอสักครู่',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    try {
      const accessToken = localStorage.getItem('accessToken')
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId
      }
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`
      }

      // แปลง betItems เป็น format ที่ backend ต้องการ (3/2 ตัวกลับ → ส่งเป็น TOP เพราะเลขขยายแล้ว)
      const betItemsForValidation = betItems.map(item => {
        const code = betTypeToPrizeTypeCode(item.betType, item.betTypeLabel)
        return {
          prizeTypeCode: prizeTypeCodeForApi(code),
          number: item.number,
          amount: item.amount
        }
      })

      // เรียก API validate-bet
      const response = await axios.post(
        withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/validate-bet`),
        {
          roundId: round._id,
          packageCode: selectedPackage?.code,
          betItems: betItemsForValidation
        },
        { headers }
      )

      Swal.close()

      if (!response.data.success) {
        toast('เกิดข้อผิดพลาดในการตรวจสอบ', {
          position: 'top-center',
          duration: 3000,
          icon: '❌'
        })
        return
      }

      const { validatedItems, summary } = response.data.data

      const lotteryCode = round?.huayName || round?.huayTypeName || 'ไม่ระบุ'
      const roundDisplay = formatRoundDisplay(round)

      // ตรวจสอบว่าเลขนี้มีการปรับอัตราจ่าย (custom payout) จาก round limits หรือไม่
      const hasCustomPayoutRate = (prizeTypeCode: string, number: string): boolean => {
        if (!round?.roundLimits?.prizeTypeLimits) return false
        const limit = round.roundLimits.prizeTypeLimits.find((l: any) => l.prizeTypeCode === prizeTypeCode)
        if (!limit?.numberSettings) return false
        const setting = limit.numberSettings.find((ns: any) => ns.number === number)
        return setting?.customPayoutRate != null
      }

      // คำนวณยอดแทงรวมและส่วนลดจากเฉพาะรายการที่พร้อมส่ง (ไม่รวมเลขอั้นจ่าย)
      let totalBetAmount = 0
      let totalDiscount = 0
      validatedItems.forEach((item: any, index: number) => {
        const isBlocked = item.isBlocked || !item.isValid
        if (isBlocked) return
        const amt = item.amount ?? betItems[index]?.amount ?? 0
        totalBetAmount += amt
        totalDiscount += amt * (item.discount || 0) / 100
      })

      // นับว่ามีรายการที่ปรับอัตราจ่ายหรือไม่ (สำหรับแสดงคำอธิบาย)
      const hasAnyAdjustedRate = validatedItems.some((validatedItem: any, index: number) => {
        if (validatedItem.isBlocked || !validatedItem.isValid) return false
        const originalItem = betItems[index]
        const number = validatedItem.number || originalItem?.number || ''
        const prizeTypeCode = originalItem ? betTypeToPrizeTypeCode(originalItem.betType, originalItem.betTypeLabel) : ''
        return hasCustomPayoutRate(prizeTypeCode, number)
      })

      // สร้างตารางรายละเอียดเลขที่แทง (ใช้ข้อมูลจาก validated items)
      const betItemsTable = `
        <div style="max-height: 400px; overflow-y: auto; margin: 10px 0;">
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr style="background-color: #f8f9fa; border-bottom: 2px solid #dee2e6;">
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">ลำดับ</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">เลข</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">ชนิด</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">เงินแทง (฿)</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">จ่าย (฿)</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">ลด (%)</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              ${validatedItems.map((validatedItem: any, index: number) => {
                const originalItem = betItems[index]
                const isBlocked = validatedItem.isBlocked || !validatedItem.isValid
                const payout = validatedItem.potentialPayout || 0
                const number = validatedItem.number || originalItem?.number || ''
                const prizeTypeCode = originalItem ? betTypeToPrizeTypeCode(originalItem.betType, originalItem.betTypeLabel) : ''
                const isAdjustedRate = !isBlocked && hasCustomPayoutRate(prizeTypeCode, number)
                const rowColor = isBlocked ? '#ffebee' : (isAdjustedRate ? '#fff8e1' : '')
                const limitInfo = validatedItem.limitInfo || null
                const remainingHint = isBlocked && limitInfo && Number(limitInfo.remainingAllowed) > 0
                  ? `<br/><span style="display:inline-block; margin-top:4px; color:#8a4b00;">ซื้อเพิ่มได้อีก ${formatAmountDisplay(Number(limitInfo.remainingAllowed))} บาท</span>`
                  : ''
                const statusText = isBlocked 
                  ? `<span style="color: #e74c3c; font-weight: bold;">${formatBlockedReason(validatedItem.reason)}${remainingHint}</span>`
                  : (isAdjustedRate ? '<span style="color: #e65100;">✓ พร้อมส่ง (อัตราปรับ)</span>' : '<span style="color: #28a745;">✓ พร้อมส่ง</span>')
                
                return `
                  <tr style="border-bottom: 1px solid #dee2e6; background-color: ${rowColor};">
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">${index + 1}</td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6; font-weight: bold; color: ${isBlocked ? '#e74c3c' : (isAdjustedRate ? '#e65100' : '#28a745')}; ${isAdjustedRate ? 'background-color: #ffecb3;' : ''}">
                      ${number || '-'}
                    </td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">${originalItem?.betTypeLabel || originalItem?.betType || '-'}</td>
                    <td style="padding: 8px; text-align: right; border: 1px solid #dee2e6;">${(isBlocked ? 0 : (validatedItem.amount || originalItem?.amount || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 8px; text-align: right; border: 1px solid #dee2e6; color: ${isBlocked ? '#e74c3c' : (isAdjustedRate ? '#e65100' : '#28a745')}; font-weight: bold; ${isAdjustedRate ? 'background-color: #ffecb3;' : ''}">
                      ${payout.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">${(isBlocked ? 0 : (validatedItem.discount || 0)).toFixed(0)}</td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6; font-size: 12px;">${statusText}</td>
                  </tr>
                `
              }).join('')}
            </tbody>
          </table>
        </div>
        ${hasAnyAdjustedRate ? '<p style="margin: 8px 0 0 0; font-size: 12px; color: #e65100;"><span style="display: inline-block; width: 12px; height: 12px; background-color: #ffecb3; border: 1px solid #ffb74d; vertical-align: middle; margin-right: 4px;"></span> แถบสี = เลขที่ปรับอัตราจ่าย</p>' : ''}
      `

      // แสดง warning ถ้ามีเลขอั้นจ่าย
      const validItems = Number(summary?.validItems || 0)
      const invalidItems = Number(summary?.invalidItems || 0)
      const hasSendableItems = validItems > 0
      const warningMessage = invalidItems > 0
        ? `<p style="color: #e74c3c; font-size: 14px; text-align: center; margin: 10px 0; background-color: #ffebee; padding: 10px; border-radius: 5px;">
            <i class="fas fa-exclamation-triangle"></i> มีรายการไม่ผ่านเงื่อนไข ${invalidItems} รายการ กรุณาตรวจสอบก่อนยืนยัน
          </p>`
        : ''

      Swal.fire({
        title: `รายการทั้งหมด : ${betItems.length} รายการ`,
        html: `
          <div style="text-align: left; padding: 10px;">
            <div style="background-color: #f8f9fa; padding: 12px; border-radius: 8px; margin-bottom: 15px;">
              <p style="margin: 5px 0;"><strong>🎯 หวย:</strong> ${lotteryCode}</p>
              <p style="margin: 5px 0;"><strong>🔄 รอบวันที่:</strong> ${roundDisplay}</p>
              <p style="margin: 5px 0;"><strong>💰 รวมแทง:</strong> <span style="color: #e74c3c; font-weight: bold;">${totalBetAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</span></p>
              <p style="margin: 5px 0;"><strong>💸 รวมส่วนลด:</strong> ${totalDiscount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</p>
              <p style="margin: 5px 0;"><strong>💵 รวมเป็นเงิน:</strong> <span style="color: #28a745; font-weight: bold;">${(totalBetAmount - totalDiscount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</span></p>
              <p style="margin: 5px 0;"><strong>📝 จำนวน:</strong> ${betItems.length} รายการ (พร้อมส่ง: ${validItems}, ไม่ผ่านเงื่อนไข: ${invalidItems})</p>
            </div>
            
            <div style="margin: 15px 0;">
              <p style="font-weight: bold; margin-bottom: 10px; color: #333;">📋 รายละเอียดเลขที่แทง:</p>
              ${betItemsTable}
            </div>
            
            ${warningMessage}
            
            <hr style="margin: 15px 0; border-color: #dee2e6;">
            <p style="color: #e74c3c; font-size: 14px; text-align: center; margin: 10px 0;">
              <i class="fas fa-warning"></i> ${hasSendableItems ? 'กรุณาตรวจสอบข้อมูลให้ถูกต้องก่อนยืนยัน' : 'ไม่มีรายการที่สามารถส่งเดิมพันได้ กรุณากลับไปแก้ไข'}
            </p>
          </div>
        `,
        icon: invalidItems > 0 ? 'warning' : 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#dc3545',
        confirmButtonText: hasSendableItems ? '<i class="fas fa-check"></i> ยืนยันการส่ง' : '<i class="fas fa-ban"></i> ไม่มีรายการส่งได้',
        cancelButtonText: '<i class="fas fa-arrow-left"></i> กลับไปแก้ไข',
        reverseButtons: true,
        width: '800px',
        customClass: {
          popup: 'swal2-popup-custom'
        },
        focusConfirm: hasSendableItems,
        focusCancel: !hasSendableItems,
        didOpen: () => {
          if (!hasSendableItems) {
            const confirmButton = Swal.getConfirmButton()
            if (confirmButton) confirmButton.disabled = true
          }
        }
      }).then((result) => {
        if (result.isConfirmed && submitBetItems) {
          submitBetItems()
        }
      })

    } catch (error: any) {
      Swal.close()
      console.error('Error validating bet items:', error)
      toast(formatBlockedReason(error.response?.data?.message) || 'เกิดข้อผิดพลาดในการตรวจสอบเลข', {
        position: 'top-center',
        duration: 4000,
        icon: '❌'
      })
    }
  }, [betItems, round, selectedPackage, submitBetItems, totalBetAmount, walletBalance, formatCurrency, isSubmittingBet])

  // ฟังก์ชันล้างข้อมูลทั้งหมดพร้อม confirmation
  const handleClearAllBetItems = useCallback(() => {
    if (!clearAllBetItems) return

    if (betItems.length === 0) {
      toast('ไม่มีรายการเดิมพันให้ล้าง', {
        position: 'top-center',
        icon: 'ℹ️'
      })
      return
    }

    const lotteryCode = round?.huayName || round?.huayTypeName || 'ไม่ระบุ'
    const roundDisplay = formatRoundDisplay(round)

    Swal.fire({
      title: 'ยืนยันการล้างรายการทั้งหมด',
      html: `
        <div style="text-align: center;">
          <p><i class="fas fa-exclamation-triangle" style="color: #f39c12; font-size: 48px;"></i></p>
          <p><strong>คุณต้องการล้างรายการเดิมพันทั้งหมดใช่หรือไม่?</strong></p>
          <p style="color: #666;">หวย: <strong>${lotteryCode}</strong></p>
          <p style="color: #666;">${roundDisplay}</p>
          <p style="color: #666;">จำนวนรายการที่จะลบ: <strong>${
            betItems.length
          } รายการ</strong></p>
          <p style="color: #e74c3c; font-size: 14px;">
            <i class="fas fa-warning"></i> การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: '<i class="fas fa-trash"></i> ล้างทั้งหมด',
      cancelButtonText: '<i class="fas fa-times"></i> ยกเลิก',
      reverseButtons: true
    }).then(result => {
      if (result.isConfirmed) {
        // ล้างข้อมูลใน component
        clearAllBetItems()

        // ปิดการไฮไลท์ถ้าเปิดอยู่
        setDuplicateHighlight({
          enabled: false,
          sameAmountItems: [],
          differentAmountItems: []
        })

        toast.success(
          `🗑️ ล้างรายการเดิมพันทั้งหมดเรียบร้อยแล้ว\n🎯 ${lotteryCode} ${roundDisplay}`,
          {
            position: 'top-center',
            duration: 3000
          }
        )
      }
    })
  }, [betItems, clearAllBetItems, round])

  // Load Yeekee shots data (โหลดทุก 15 วินาที; โหลดแบบ silent ไม่ทำให้ UI กระพริบ)
  useEffect(() => {
    if (!isYeekee || !round?._id) return

    hasLoadedShotsOnce.current = false
    const roundId = round._id
    const loadYeekeeShots = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      const isFirstLoad = !hasLoadedShotsOnce.current
      try {
        if (isFirstLoad) setLoadingShots(true)
        const accessToken = localStorage.getItem('accessToken')
        
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantId
        }
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`
        }

        const response = await axios.get(
          withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/yeekee/rounds/${roundId}/shots`),
          { headers }
        )

        if (response.data.success) {
          const shots = response.data.shots || []
          setYeekeeShots(shots)
        }
      } catch (error: any) {
        console.error('Error loading Yeekee shots:', error)
      } finally {
        if (isFirstLoad) {
          hasLoadedShotsOnce.current = true
          setLoadingShots(false)
        }
      }
    }

    loadYeekeeShots()
    
    const intervalId = setInterval(loadYeekeeShots, 15 * 1000)
    return () => clearInterval(intervalId)
  }, [isYeekee, round?._id])

  // Update countdown timer
  useEffect(() => {
    if (!isYeekee || !round?.betEndTime) return

    const updateCountdown = () => {
      const now = new Date()
      const endTime = new Date(round.betEndTime)
      const diff = endTime.getTime() - now.getTime()

      if (diff <= 0) {
        setCountdown('00:00:00')
        return
      }

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setCountdown(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      )
    }

    updateCountdown()
    const intervalId = setInterval(updateCountdown, 1000)
    return () => clearInterval(intervalId)
  }, [isYeekee, round?.betEndTime])

  // Format bet end time
  const betEndTimeFormatted = useMemo(() => {
    if (!round?.betEndTime) return ''
    const date = new Date(round.betEndTime)
    return date.toLocaleString('th-TH', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }, [round?.betEndTime])

  // Handle submit prediction
  const handleSubmitPrediction = async () => {
    if (!predictionNumber || predictionNumber.length !== yeekeeNumberDigits || !round?._id) {
      if (!round?._id) return
      if (!predictionNumber || predictionNumber.length < yeekeeNumberDigits) {
        toast.error(`กรุณาใส่เลขให้ครบ ${yeekeeNumberDigits} หลัก`)
      }
      return
    }

    try {
      const accessToken = localStorage.getItem('accessToken')
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId
      }
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`
      }

      // Get user ID, username, เบอร์โทร (แสดง username ก่อน ไม่มีค่อย mobile — คล้ายบอท)
      const userStr = localStorage.getItem('user')
      const user = userStr ? JSON.parse(userStr) : null
      const userId = user?.id || user?.userId
      const username = user?.username || user?.name || undefined
      const displayPhoneNumber = user?.mobile || user?.phone || undefined

      const response = await axios.post(
        withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/yeekee/shoot`),
        {
          roundId: round._id,
          number: predictionNumber,
          userId,
          ...(username ? { username } : {}),
          ...(displayPhoneNumber ? { displayPhoneNumber } : {})
        },
        { headers }
      )

      if (response.data.success) {
        // Clear input and reload shots
        setPredictionNumber('')
        toast.success('ส่งเลขออกรางวัลเรียบร้อย')
        // Reload shots after a short delay
        setTimeout(() => {
          const loadShots = async () => {
            try {
              const shotsResponse = await axios.get(
                withApiBaseUrl(`/api/v1/lottery/tenant/${tenantId}/yeekee/rounds/${round._id}/shots`),
                { headers }
              )
              if (shotsResponse.data.success) {
                const shots = shotsResponse.data.shots || []
                setYeekeeShots(shots)
              }
            } catch (error) {
              console.error('Error reloading shots:', error)
            }
          }
          loadShots()
        }, 500)
      }
    } catch (error: any) {
      console.error('Error submitting prediction:', error)
      toast.error(error.response?.data?.message || error.response?.data?.error || 'เกิดข้อผิดพลาดในการส่งเลข')
    }
  }

  const payoutRatesData = useMemo(
    () => buildPayoutRatesTableData(selectedPackage?.prizeConfigs),
    [selectedPackage]
  )

  return (
    <div className="col-12 col-lg-6 px-1">
      <div>
        <div className="row mx--1">
          <div className="col-12 px-1 mb-2">
            <div className="bdr-5 bgc-4 px-2 py-1 wrap justify-content-between">
              <div className="wrap t-medium c-red">
                <i className="far fa-bookmark me-2"></i>
                ใส่ราคา
              </div>
              <div className="bgc-9 bdr-5 px-2 c-white wrap justify-content-center">
                <p>
                  {betItems.length > 0
                    ? `${betItems.length} รายการ`
                    : 'ไม่มี รายการ'}
                </p>
              </div>
            </div>
          </div>

          {/* แสดงตารางเดิมพันตามประเภทที่เลือก */}
          <div className="col-12 px-1 mb-2">
            {betItems.length > 0 && (
              <div
                className="bdr-5 px-2 py-2 mb-2"
                style={{
                  backgroundColor: isInlineValidating ? '#eef6ff' : '#f8f9fa',
                  color: isInlineValidating ? '#0d6efd' : '#6c757d',
                  fontSize: '12px'
                }}
              >
                <i className={`fas ${isInlineValidating ? 'fa-spinner fa-spin' : 'fa-shield-alt'} me-2`} />
                {isInlineValidating
                  ? 'กำลังตรวจสอบยอดซื้อสะสมและลิมิตของแต่ละเลข...'
                  : 'ระบบจะแจ้งเตือนอัตโนมัติเมื่อยอดซื้อเกินลิมิต และสามารถกดปรับราคาได้ทันที'}
              </div>
            )}
            {Object.keys(groupedBetItems).length > 0 ? (
              getSortedBetTypes(groupedBetItems).map(
                ([betType, items], groupIndex) => {
                  // ใช้ betTypeLabel จาก item แรกในกลุ่ม แทนการ map จาก betType
                  const betTypeLabel = items[0]?.betTypeLabel || betType

                  return (
                    <div
                      className="wrap justify-content-center bgc-4 c-white px-2 py-2 bdr-5 fs-14 mb-2"
                      key={betType}
                    >
                      <div className="table-responsive w-100">
                        <table
                          className="table table-w table-striped mb-0 table-bordered text-center"
                          style={{ tableLayout: 'fixed' }}
                        >
                          <thead className="table-dark">
                            <tr>
                              <th style={{ width: '8%' }}>##</th>
                              <th style={{ width: '20%' }}>
                                <div className="bgg-5 c-white bdr-5">
                                  {betTypeLabel}
                                </div>
                              </th>
                              <th style={{ width: '20%' }}>แทง (฿)</th>
                              <th style={{ width: '15%' }}>คูณ</th>
                              <th style={{ width: '20%' }}>ชนะ</th>
                              <th style={{ width: '12%' }}>ลบ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => {
                              // ใส่ ref เฉพาะ input แรกของ group แรกเท่านั้น
                              const isFirstInput = groupIndex === 0 && index === 0

                              // ตรวจสอบสถานะการไฮไลท์รายการซ้ำ
                              const isDuplicateWithSameAmount =
                                duplicateHighlight.enabled &&
                                duplicateHighlight.sameAmountItems.includes(item.id)
                              const isDuplicateWithDifferentAmount =
                                duplicateHighlight.enabled &&
                                duplicateHighlight.differentAmountItems.includes(item.id)
                              const validatedItem = inlineValidationByItemId[item.id]
                              const hasFreshValidation = hasFreshInlineValidation(item, validatedItem)
                              const isPendingValidation = !hasFreshValidation
                              const isHardBlockedItem = hasFreshValidation && isHardBlockedValidation(validatedItem)
                              const validationNotice = hasFreshValidation ? getInlineValidationNotice(validatedItem) : null
                              const suggestedAmount = validationNotice?.suggestedAmount || 0
                              const displayPayout = hasFreshValidation && validatedItem?.isValid
                                ? (validatedItem.potentialPayout ?? item.payout)
                                : 0
                              const rowBackgroundColor = isDuplicateWithSameAmount
                                ? '#ffebee'
                                : isDuplicateWithDifferentAmount
                                ? '#fff3e0'
                                : isPendingValidation
                                ? '#fff8e1'
                                : validatedItem && !validatedItem.isValid
                                ? '#fff5f5'
                                : 'transparent'

                              return (
                                <React.Fragment key={item.id}>
                                  <tr
                                    style={{
                                      backgroundColor: rowBackgroundColor
                                    }}
                                  >
                                    <th>
                                      <p className="mb-0">{index + 1}.</p>
                                    </th>
                                    <th>
                                      <p className="bgg-1 c-white bdr-5 p-2 mb-0">
                                        {item.number}
                                      </p>
                                    </th>
                                    <th>
                                      <div className="bdc-1 bgc-4 c-dark bdr-5 py-1 px-1 w-100">
                                        <input
                                          type="number"
                                          value={isHardBlockedItem ? 0 : item.amount}
                                          onChange={e =>
                                            handleAmountChange(item.id, e.target.value)
                                          }
                                          placeholder="1"
                                          maxLength={4}
                                          autoComplete="off"
                                          className="border-0 w-100 text-center"
                                          min="1"
                                          disabled={isHardBlockedItem}
                                          ref={isFirstInput ? firstInputRef : null}
                                        />
                                      </div>
                                    </th>
                                    <th>
                                      <p className="c-dark mb-0">
                                        {hasFreshValidation && validatedItem?.isValid ? formatAmountDisplay(validatedItem.payoutRate ?? item.multiplier) : '-'}
                                      </p>
                                    </th>
                                    <th>
                                      <p className={`bdr-5 p-2 mb-0 ${validatedItem && !validatedItem.isValid ? 'bg-danger-subtle text-danger' : 'bgc-7 c-dark'}`}>
                                        {isPendingValidation ? 'รอตรวจสอบ' : formatAmountDisplay(displayPayout, true)}
                                      </p>
                                    </th>
                                    <th>
                                      <button
                                        className="bgg-1 py-2 px-2 border-0 bdr-5 w-100"
                                        onClick={() => removeBetItem && removeBetItem(item.id)}
                                      >
                                        <i className="fas fa-trash-alt c-white" />
                                      </button>
                                    </th>
                                  </tr>
                                  {validationNotice && (
                                    <tr>
                                      <td colSpan={6} style={{ backgroundColor: '#fff8e1' }}>
                                        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 py-2 px-2">
                                          <div style={{ fontSize: '12px', color: '#8a4b00', textAlign: 'left' }}>
                                            <div style={{ fontWeight: 700 }}>
                                              <i className="fas fa-exclamation-triangle me-1" />
                                              {validationNotice.title}
                                            </div>
                                            {validationNotice.detail && (
                                              <div>{validationNotice.detail}</div>
                                            )}
                                          </div>
                                          {suggestedAmount > 0 && updateBetAmount && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-warning"
                                              onClick={() => updateBetAmount(item.id, suggestedAmount)}
                                            >
                                              ปรับเป็น {formatAmountDisplay(suggestedAmount)} บาท
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                }
              )
            ) : (
              <div className="wrap justify-content-center bgc-4 c-white px-2 py-2 bdr-5 fs-14">
                <div className="table-responsive w-100">
                  <table
                    className="table table-w table-striped mb-0 table-bordered text-center"
                    style={{ tableLayout: 'fixed' }}
                  >
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: '8%' }}>##</th>
                        <th style={{ width: '20%' }}>
                          <div className="bgg-5 c-white bdr-5">
                            ประเภทการเล่น
                          </div>
                        </th>
                        <th style={{ width: '20%' }}>แทง (฿)</th>
                        <th style={{ width: '15%' }}>คูณ</th>
                        <th style={{ width: '20%' }}>ชนะ</th>
                        <th style={{ width: '12%' }}>ลบ</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={6} className="text-center p-4">
                          <div>
                            <p className="mb-2 c-dark">
                              <i className="fas fa-info-circle me-2"></i>
                              ยังไม่มีรายการเดิมพัน
                            </p>
                            <small className="text-muted">
                              กรุณาเลือกประเภทการเล่นและระบุตัวเลขในฝั่งซ้าย
                            </small>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ปุ่มล้างรายการทั้งหมด */}
          {betItems.length > 0 && (
            <div className="col-12 px-1 mb-2">
              <button
                className="btn btn-danger w-100"
                onClick={handleClearAllBetItems}
              >
                <i className="fas fa-trash me-2"></i>
                ล้างรายการทั้งหมด
              </button>
            </div>
          )}

          {/* ดูเลขซ้ำ */}
          <div className="col-6 col-md-6 px-1 mb-2">
            <button
              className={`check-button border-0 w-100 ${
                duplicateHighlight.enabled ? 'bgg-5' : 'bgg-4'
              }`}
              onClick={
                duplicateHighlight.enabled
                  ? clearDuplicateHighlight
                  : checkDuplicateItems
              }
              disabled={betItems.length === 0}
            >
              <p className="c-white">
                <i
                  className={`fas ${
                    duplicateHighlight.enabled ? 'fa-eye-slash' : 'fa-bolt'
                  } me-2`}
                />
                {duplicateHighlight.enabled ? 'ปิดการแสดงซ้ำ' : 'ดูเลขซ้ำ'}
              </p>
            </button>
          </div>

          {/* ตัดเลขซ้ำ */}
          <div className="col-6 col-md-6 px-1 mb-2">
            <button
              className="check-button border-0 bgg-1 w-100"
              onClick={removeDuplicateItems}
              disabled={betItems.length === 0}
            >
              <p className="c-white">
                <i className="fas fa-trash-alt me-2" />
                ตัดเลขซ้ำ
              </p>
            </button>
          </div>

          <div className="col-12 px-1 mb-2">
            <div className="wrap justify-content-center bgc-4 c-white px-1 py-2 bdr-5">
              <div className="row mx--1 align-items-center w-100">
                <div className="col-12 px--2 mb-2">
                  <h6 className="c-dark text-center t-regular">
                    ใส่ราคาเท่ากันหมด
                  </h6>
                </div>
                <div className="col-12 col-md-4 px--2 mb-2 mb-md-0">
                  <div className="wrap">
                    <input
                      type="text"
                      id="amount"
                      autoComplete="off"
                      placeholder="จำนวน"
                      value={bulkAmount}
                      onChange={e => setBulkAmount(e.target.value)}
                      className="bdc-1 bdr-5 p-2"
                      style={{ width: '65%' }}
                    />
                    <button
                      className="py-2 bgg-1 border-0 bdr-5 c-white"
                      style={{ width: '35%' }}
                      onClick={() => handleBulkAmountUpdate(bulkAmount)}
                      disabled={!bulkAmount || betItems.length === 0}
                    >
                      <i className="fas fa-plus-square" />
                    </button>
                  </div>
                </div>
                <div className="col-12 col-md-8 px--2">
                  <div>
                    <div className="row mx--2">
                      <div className="col-2 px--2">
                        <button
                          className="bgc-7 bdr-5 bdc-4 px-0 py-2 c-dark w-100"
                          onClick={() => handleBulkAmountUpdate(5)}
                          disabled={betItems.length === 0}
                        >
                          <p>5฿</p>
                        </button>
                      </div>
                      <div className="col-2 px--2">
                        <button
                          className="bgc-7 bdr-5 bdc-4 px-0 py-2 c-dark w-100"
                          onClick={() => handleBulkAmountUpdate(10)}
                          disabled={betItems.length === 0}
                        >
                          <p>10฿</p>
                        </button>
                      </div>
                      <div className="col-2 px--2">
                        <button
                          className="bgc-7 bdr-5 bdc-4 px-0 py-2 c-dark w-100"
                          onClick={() => handleBulkAmountUpdate(20)}
                          disabled={betItems.length === 0}
                        >
                          <p>20฿</p>
                        </button>
                      </div>
                      <div className="col-2 px--2">
                        <button
                          className="bgc-7 bdr-5 bdc-4 px-0 py-2 c-dark w-100"
                          onClick={() => handleBulkAmountUpdate(50)}
                          disabled={betItems.length === 0}
                        >
                          <p>50฿</p>
                        </button>
                      </div>
                      <div className="col-2 px--2">
                        <button
                          className="bgc-7 bdr-5 bdc-4 px-0 py-2 c-dark w-100"
                          onClick={() => handleBulkAmountUpdate(100)}
                          disabled={betItems.length === 0}
                        >
                          <p>100฿</p>
                        </button>
                      </div>
                      <div className="col-2 px--2">
                        <button
                          className="bgc-7 bdr-5 bdc-4 px-0 py-2 c-dark w-100"
                          onClick={() => handleBulkAmountUpdate(500)}
                          disabled={betItems.length === 0}
                        >
                          <p>500฿</p>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 px-1 mb-2">
            <div className="p-1 bgg-3 bdr-5 c-white w-100 text-center">
              <small className="bgc-4 px-2 c-blue bdr-5">
                ยอดเครดิตคงเหลือ
              </small>
              <h5 className="t-medium">
                {isLoadingWallet ? '...' : formatCurrency(walletBalance)}
              </h5>
            </div>
          </div>

          <div className="col-6 px-1 mb-2">
            <div className="p-1 bgg-1 bdr-5 c-white w-100 text-center">
              <small className="bgc-4 px-2 c-red bdr-5">รวมยอดแทง</small>
              <h5 className="t-medium">{totalBetAmount.toLocaleString()}</h5>
            </div>
          </div>

          {/* ยกเลิกทั้งหมด */}
          <div className="col-6 px-1 mb-2">
            <button
              className="bgg-5 py-2 w-100 border-0 bdr-5"
              onClick={handleClearAllBetItems}
            >
              <p className="c-white">
                <i className="fas fa-trash-alt me-2" />
                ยกเลิกทั้งหมด
              </p>
            </button>
          </div>

          {/* ส่งโพย */}
          <div className="col-6 px-1 mb-2">
            <button
              className="bgg-4 py-2 w-100 border-0 bdr-5"
              onClick={handleSubmitBetConfirmation}
              disabled={betItems.length === 0 || isSubmittingBet}
            >
              <p className="c-white">
                <i className="fas fa-hand-pointer me-2" />
                {isSubmittingBet ? 'กำลังส่งโพย...' : 'ส่งโพย'}
              </p>
            </button>
          </div>

          {/* Yeekee Prediction Section - Only show for Yeekee lotteries */}
          {isYeekee && (
            <>
              <div className="col-12 px-1 mb-2">
                <div className="wrap justify-content-center bgc-4 c-white p-2 bdr-5">
                  <div className="mb-2 w-100">
                    <h5 className="text-center t-regular c-dark bdc-1 px-3 bdr-10">
                      ร่วมส่งเลขทายผลการออกรางวัล
                    </h5>
                    <p className="text-center c-dark t-regular mt-2">
                      ปิดเวลา : {betEndTimeFormatted}
                    </p>
                    <h1 className="c-red text-center t-regular my-3">
                      {countdown}
                    </h1>
                    <h6 className="text-center bdc-1 p-2 t-regular c-red mx-auto" style={{ width: 'fit-content' }}>
                      ร่วมส่งเลข {yeekeeNumberDigits} หลัก
                    </h6>
                  </div>

                  <div className="wrap w-100 bgc-4 bdr-5 bdc-4 p-1 mb-2">
                    <input
                      type="text"
                      placeholder={`ใส่เลข ${yeekeeNumberDigits} หลัก`}
                      className="text-center w-100 c-dark border-0"
                      value={predictionNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, yeekeeNumberDigits)
                        setPredictionNumber(value)
                      }}
                      maxLength={yeekeeNumberDigits}
                    />
                  </div>

                  <button
                    className="bgg-2 bdr-5 py-2 border-0 c-white w-100 mb-2"
                    onClick={handleRandomPrediction}
                    type="button"
                  >
                    <i className="fas fa-random me-2"></i>สุ่มเลข
                  </button>

                  <button 
                    className="bgg-1 bdr-5 py-2 border-0 c-white w-100"
                    onClick={handleSubmitPrediction}
                    disabled={loadingShots}
                  >
                    <i className="fas fa-check me-2"></i>ส่งเลขออกรางวัล
                  </button>
                </div>
              </div>

              <div className="col-12 px-1 mb-2">
                <div className="wrap justify-content-center bgc-4 bdr-5 c-white p-2 bx-sd">
                  <div className="w-100">
                    <h5 className="text-center t-regular c-dark bdc-1 px-3 bdr-10">
                      ผลรวมเลขและสมาชิกที่ร่วมออกรางวัล
                    </h5>
                    <h1 className="c-red text-center t-medium my-2">
                      {(() => {
                        if (round?.yeekee?.result?.sumTotal != null) {
                          return String(round.yeekee.result.sumTotal)
                        }
                        const sum = yeekeeShots.reduce((acc, s) => acc + Number(s.number || 0), 0)
                        return sum > 0 ? String(sum) : '0'
                      })()}
                    </h1>
                    {round?.yeekee?.result?.sumTotal == null && yeekeeShots.length > 0 && yeekeeShots.length < 25 && (
                      <p className="text-center mb-0 small c-dark">ส่งแล้ว {yeekeeShots.length} ชุด (รางวัลต้องมี 25 ชุดขึ้นไป)</p>
                    )}
                  </div>
                  <div className="table-responsive w-100 fs-14 yeekee-shot-table-scroll-classic">
                    <table className="table table-striped mb-0 table-bordered text-center">
                      <thead className="table-dark">
                        <tr>
                          <th>ลำดับ</th>
                          <th>เลข</th>
                          <th>สมาชิก</th>
                          <th>เวลา</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingShots ? (
                          <tr>
                            <td colSpan={4} className="text-center py-3">
                              <i className="fas fa-spinner fa-spin me-2"></i>
                              กำลังโหลดข้อมูล...
                            </td>
                          </tr>
                        ) : yeekeeShots.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-center py-3 c-dark">
                              ยังไม่มีผู้ร่วมส่งเลข
                            </td>
                          </tr>
                        ) : (
                          (() => {
                            // Mask username/mobile: กลางเป็น xxxxx (kadaejung1 -> kaxxxxxng1, 0900000001 -> 090xxxxxx1)
                            const maskMember = (raw: string): string => {
                              const s = String(raw).trim()
                              if (!s || s.length < 4) return s
                              if (/^\d+$/.test(s)) {
                                if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
                                return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
                              }
                              if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
                              return s.slice(0, 2) + 'xxxxx' + s.slice(-3)
                            }
                            return yeekeeShots.map((shot, index) => {
                            const shotTime = new Date(shot.shotAt)
                            const timeStr = shotTime.toLocaleTimeString('th-TH', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })
                            // Bot: แสดงชื่อแบบ kaxxxxxng1 เท่านั้น — ไม่แสดง "สมาชิก" หรือ "บอท"
                            // User ยิงเอง: แสดงเบอร์แบบ mask
                            const isBot = String(shot.shooterType) === 'bot'
                            const botDisplayNameFallback = (id: string) => {
                              const letters = 'abcdefghijklmnopqrstuvwxyz'
                              let n = 0
                              for (let i = 0; i < (id || '').length; i++) n = (n * 31 + (id.charCodeAt(i) || 0)) >>> 0
                              const r = () => { n = (n * 1103515245 + 12345) >>> 0; return n }
                              const p = () => letters[r() % 26]
                              return p() + p() + 'xxxxx' + p() + p() + String(r() % 10)
                            }
                            // User: แสดง username ก่อน ไม่มีค่อยใช้ mobile (คล้ายรูปแบบบอท)
                            const memberName = isBot
                              ? (shot.username && shot.username.includes('xxxxx') ? shot.username : botDisplayNameFallback(shot._id != null ? String(shot._id) : String(index)))
                              : (shot.username
                                  ? maskMember(shot.username)
                                  : shot.displayPhoneNumber
                                    ? maskMember(shot.displayPhoneNumber)
                                    : 'สมาชิก')
                            const isBonus5 = index === 4
                            const isBonus20 = index === 19

                            return (
                              <tr key={shot.number + '-' + index}>
                                <td>
                                  {isBonus5 ? (
                                    <div className="bgg-4 bdr-5 c-white d-inline px-2" title="รางวัลโบนัส ลำดับ 5">
                                      {index + 1}
                                    </div>
                                  ) : isBonus20 ? (
                                    <div className="bgg-1 bdr-5 c-white d-inline px-2" title="รางวัลโบนัส ลำดับ 20">
                                      {index + 1}
                                    </div>
                                  ) : (
                                    index + 1
                                  )}
                                </td>
                                <td>{shot.number}</td>
                                <td>{memberName}</td>
                                <td>{timeStr}</td>
                              </tr>
                            )
                          })
                          })()
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ตารางรางวัล */}
          <div className="col-12 px-1">
            <div className="wrap justify-content-center bgc-4 c-white p-2 bdr-5 fs-14">
              {payoutRatesData.length > 0 ? (
                <div className="table-responsive w-100">
                  <table className="table table-striped mb-0 table-bordered text-center">
                    <thead className="table-dark">
                      <tr>
                        <th>ลำดับ</th>
                        <th>ชนิด</th>
                        <th>จ่าย(บ.)</th>
                        <th>ลด(%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutRatesData.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}.</td>
                          <td>{item.name}</td>
                          <td>{item.payout.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>{item.discount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center p-3">
                  <p className="mb-0 c-dark">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    ไม่พบข้อมูลรางวัลสำหรับหวยนี้
                  </p>
                  <small className="text-muted">
                    กรุณาติดต่อเจ้าหน้าที่หากปัญหานี้ยังคงเกิดขึ้น
                  </small>
                </div>
              )}
              
              {/* ปุ่มเปลี่ยนแปลงราคาแทง (disable เมื่อมี package เดียว) */}
              {payoutRatesData.length > 0 && (
                <div className="w-100 mt-2">
                  <button
                    className="w-100 btn btn-warning c-dark fw-bold py-2 bdr-5"
                    style={{
                      backgroundColor: '#ff9800',
                      border: 'none',
                      fontSize: '14px'
                    }}
                    disabled={packageCount !== undefined && packageCount <= 1}
                    title={packageCount !== undefined && packageCount <= 1 ? 'มีแพ็กเกจเดียว ไม่สามารถเปลี่ยนราคาได้' : undefined}
                    onClick={async () => {
                      try {
                        // ตรวจสอบว่ามี round หรือไม่
                        let currentRound = round
                        
                        // ถ้าไม่มี round จาก prop ให้ลองดึงจาก sessionStorage
                        if (!currentRound) {
                          // ลองดึงจาก sessionStorage ที่เก็บไว้ก่อนหน้า
                          const storedRoundJson = sessionStorage.getItem('lottery:roundForTerms')
                          if (storedRoundJson) {
                            try {
                              currentRound = JSON.parse(storedRoundJson) as LotteryRound
                            } catch (e) {
                              console.warn('Failed to parse stored round:', e)
                            }
                          }
                          
                          // ถ้ายังไม่มี ให้ลอง fetch จาก roundId
                          if (!currentRound) {
                            const storedRoundId = sessionStorage.getItem('lottery:lastRoundId')
                            if (storedRoundId) {
                              try {
                                const fetchedRound = await lotteryService.getRoundById(storedRoundId)
                                if (fetchedRound) {
                                  currentRound = fetchedRound
                                }
                              } catch (error) {
                                console.error('Error fetching round from sessionStorage:', error)
                              }
                            }
                          }
                        }
                        
                        // ถ้ายังไม่มี round ให้แจ้งเตือนและไม่ navigate
                        if (!currentRound) {
                          toast.error('ไม่พบข้อมูลรอบหวย กรุณาเลือกรอบใหม่', {
                            position: 'top-center',
                            duration: 3000
                          })
                          navigate('/lotto', { replace: true })
                          return
                        }
                        
                        // เก็บ round ใน sessionStorage เพื่อป้องกันกรณี refresh
                        if (typeof window !== 'undefined') {
                          try {
                            sessionStorage.setItem('lottery:lastRoundId', currentRound._id)
                            sessionStorage.setItem('lottery:roundForTerms', JSON.stringify(currentRound))
                          } catch (e) {
                            console.warn('Failed to store round in sessionStorage:', e)
                          }
                        }
                        
                        // Navigate ไปหน้า /lottery-terms พร้อมส่ง round state
                        navigate('/lottery-terms', {
                          state: {
                            round: currentRound,
                            fromBetPage: true // Flag เพื่อบอกว่ามาจากหน้า betting
                          }
                        })
                      } catch (error) {
                        console.error('Error navigating to lottery-terms:', error)
                        toast.error('เกิดข้อผิดพลาดในการเปลี่ยนหน้า กรุณาลองใหม่อีกครั้ง', {
                          position: 'top-center',
                          duration: 3000
                        })
                      }
                    }}
                  >
                    <i className="fas fa-arrows-alt-v me-2"></i>
                    เปลี่ยนแปลงราคาแทง
                  </button>
                </div>
              )}
            </div>
          </div>

          
        </div>
      </div>
    </div>
  )
}

export default LotteryBetSideRight
